import React from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  Typography,
  Badge,
  Button,
  ProgressStepper,
  Step,
  StepButton,
  StepLabel,
} from '@one-thd/sui-atomic-components';
import { ArrowForward, RadioButtonUnchecked } from '@one-thd/sui-icons';
import { windowExists, isAuthenticated, gotoLogonModule } from '../../../../common/utils/AccountUtils';
import { useImageError } from '../../utils/SweepstakesUtils';
import { useSweepstakesData } from '../../../../contexts/SweepstakesDataContext';
import { SWEEPSTAKES_MOCK_DATA } from '../../../../core/Data/SweepstakesMockData';
import { SWEEPSTAKES_CONSTANTS } from '../../../../core/Constants';
import { mapSweepstakesData } from '../../utils/SweepstakesDataMapper';
import { SweepstakesMarkdown } from '../../utils/SweepstakesMarkdown';
import {
  RenderGenericError,
  RenderSweepstakesCardLoading,
  RenderCompletionAlert
} from '../SweepstakesUtils';

// Utility functions
const getHeaderClasses = (showOutsideHeaderOn, showInsideHeaderOn) => {
  const buildClasses = (breakpoints) => {
    if (!breakpoints || (Array.isArray(breakpoints) && breakpoints.length === 0)) {
      return 'sui-hidden';
    }

    const bps = Array.isArray(breakpoints) ? breakpoints : [breakpoints];
    const hasMW = bps.includes('MW');
    const hasTW = bps.includes('TW');
    const hasDT = bps.includes('DT');

    const classes = [];

    // Set default mobile visibility
    if (hasMW) {
      classes.push('sui-block');
    } else {
      classes.push('sui-hidden');
    }

    // Set tablet visibility (sm breakpoint)
    if (hasTW) {
      classes.push('sm:sui-block');
    } else {
      classes.push('sm:sui-hidden');
    }

    // Set desktop visibility (lg breakpoint)
    if (hasDT) {
      classes.push('lg:sui-block');
    } else {
      classes.push('lg:sui-hidden');
    }

    return classes.join(' ');
  };

  return {
    outside: buildClasses(showOutsideHeaderOn),
    inside: buildClasses(showInsideHeaderOn),
  };
};
const formatExpirationDate = (dateString) => {
  if (!dateString) return '';

  const expirationDate = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  expirationDate.setHours(0, 0, 0, 0);

  if (expirationDate.getTime() === today.getTime()) {
    return 'Ends Today';
  }

  return `Ends ${expirationDate.toLocaleDateString()}`;
};

// Render components
const RenderLogo = ({ logoUrl, logoFallback, imgRetryCounter }) => {
  if (!logoUrl) return null;

  return logoFallback.hasError ? (
    <img
      src={SWEEPSTAKES_CONSTANTS.URLS.LOGO_FALLBACK}
      alt="Logo"
      className="sui-w-12 sui-flex-shrink-0"
      width="48"
      height="auto"
      data-testid="sweepstakes-logo-fallback"
    />
  ) : (
    <img
      key={logoUrl + imgRetryCounter}
      src={logoUrl}
      alt="Logo"
      className="sui-w-12 sui-flex-shrink-0"
      width="48"
      height="auto"
      onError={logoFallback.handleError}
      data-testid="sweepstakes-logo"
    />
  );
};

const RenderStatusBadge = ({ status }) => (
  <Badge color="success" variant="outline" data-testid="sweepstakes-status-badge">
    {isAuthenticated() ? status || 'New' : 'New'}
  </Badge>
);

const RenderTitle = ({ title }) => {
  if (!title) return null;

  return (
    <Typography variant="h6" weight="bold" height="snug" data-testid="sweepstakes-title">
      {title}
    </Typography>
  );
};

const RenderDescription = ({ description }) => {
  if (!description) return null;

  return (
    <div className="sui-mb-2" data-testid="sweepstakes-description">
      <SweepstakesMarkdown markdownText={description} disableSpacing />
    </div>
  );
};

const RenderExpiration = ({ expirationDate }) => {
  if (!expirationDate) return null;

  return (
    <Typography variant="body-base" data-testid="sweepstakes-expiration">
      {formatExpirationDate(expirationDate)}
    </Typography>
  );
};

const RenderSignInButton = ({ onClick }) => (
  <div className="sui-pt-2">
    <Button
      variant="primary"
      size="small"
      onClick={onClick}
      data-testid="sweepstakes-sign-in-button"
    >
      Sign-In to Enter!
    </Button>
  </div>
);

const RenderDetailsButton = ({ onClick }) => (
  <button
    type="button"
    className="sui-text-brand sui-p-2 sui-cursor-pointer sui-rounded-md sui-transition-all hover:sui-bg-gray-50"
    onClick={onClick}
    aria-label="View sweepstakes details"
    data-testid="sweepstakes-details-button"
  >
    <ArrowForward />
  </button>
);

const RenderStepButton = ({
  step, index, isOptedIn, onStepClick, onFirstStepClick
}) => {
  const stepIcon = useImageError();
  const isFirstStep = index === 0;

  // Determine if button should be clickable
  // First step: clickable only if opted in and not completed
  // Other steps: always clickable
  const isClickable = isFirstStep
    ? (isOptedIn && !step.completed)
    : true;

  const clickFunction = isFirstStep ? () => onFirstStepClick(step) : () => onStepClick(step);
  const handleClick = isClickable ? clickFunction : undefined;

  return (
    <Step key={step.id} completed={step.completed} data-testid={`sweepstakes-step-${step.id}`}>
      <StepButton
        onClick={handleClick}
        icon={() => (
          stepIcon.hasError ? (
            <RadioButtonUnchecked
              data-testid={`sweepstakes-step-icon-${step.id}-fallback`}
            />
          ) : (
            <img
              src={step.iconUrl}
              alt={`Step ${index + 1}`}
              width={28}
              height={28}
              className="sui-rounded-full"
              onError={stepIcon.handleError}
              data-testid={`sweepstakes-step-icon-${step.id}`}
            />
          )
        )}
      />
      <StepLabel title={step.title} />
    </Step>
  );
};

const RenderProgressSection = ({ steps, isOptedIn, onStepClick, onFirstStepClick }) => {
  // Null check for steps - non-opted users won't have steps
  if (!steps || !Array.isArray(steps) || steps.length === 0) {
    return null;
  }

  return (
    <div data-testid="sweepstakes-progress-section">
      <ProgressStepper
        centerEdgeNodes
        nonLinear
        connector={<></>}
        data-testid="sweepstakes-progress-stepper"
      >
        {steps.map((step, index) => (
          <RenderStepButton
            key={step.id}
            step={step}
            index={index}
            isOptedIn={isOptedIn}
            onStepClick={onStepClick}
            onFirstStepClick={onFirstStepClick}
          />
        ))}
      </ProgressStepper>
    </div>
  );
};

const RenderHeaderLink = ({ href, openNewWindow }) => (
  <a
    href={href}
    target={openNewWindow ? '_blank' : '_self'}
    rel={openNewWindow ? 'noopener noreferrer' : undefined}
    className="sui-cursor-pointer sui-underline"
    data-testid="sweepstakes-faqs-link"
    aria-label="View sweepstakes frequently asked questions"
  >
    Sweeps FAQs
  </a>
);

const RenderHeaderButton = ({ onClick }) => (
  <button
    type="button"
    className="sui-cursor-pointer sui-underline"
    onClick={onClick}
    data-testid="sweepstakes-faqs-link"
    aria-label="View sweepstakes frequently asked questions (opens in new tab)"
  >
    Sweeps FAQs
  </button>
);

const RenderHeader = ({ href, onClick, openNewWindow }) => (
  <div className="sui-flex sui-items-center sui-justify-between sui-mb-4">
    <Typography variant="h5" data-testid="sweepstakes-header-title">
      Sweepstakes Available (1)
    </Typography>
    <div>
      {href && <RenderHeaderLink href={href} openNewWindow={openNewWindow} />}
      {!href && onClick && <RenderHeaderButton onClick={onClick} />}
    </div>
  </div>
);

const RenderErrorCard = ({ refresh }) => {
  const testIdStr = 'sweepstakes-entry-card-error';
  return (
    <Card grow>
      <RenderGenericError refresh={refresh} testId={testIdStr} />
    </Card>
  );
};

const RenderLoadingCard = () => (
  <Card grow data-testid="sweepstakes-entry-card">
    <RenderSweepstakesCardLoading />
  </Card>
);

export const SweepstakesEntryCard = ({ SweepstakesEntryCardHeaderProps }) => {
  const { sweepstakesData, isLoading, hasError, refresh } = useSweepstakesData();

  // For unauthenticated users, use dedicated unauthenticated mock data if no sweepstakesData is provided
  // sweepstakesData is already mapped in the fetch hook
  const displayData = sweepstakesData
    || (windowExists() && !isAuthenticated() ? mapSweepstakesData(SWEEPSTAKES_MOCK_DATA.unauthenticated) : null);

  // Handle logo fallback - use generic image error hook
  const logoFallback = useImageError();

  // Create useState counter and useEffect to increment once
  const [imgRetryCounter, setCounter] = React.useState(0);
  React.useEffect(() => {
    if (imgRetryCounter === 0) setCounter((prev) => prev + 1);
  }, [imgRetryCounter]);

  const handleDetailsClick = React.useCallback((event) => {
    event.stopPropagation();
    if (!isLoading && !hasError && windowExists()) {
      // Determine which drawer to open based on sweepstakes status
      if (displayData && displayData.isOptedIn) {
        window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('loyalty-benefits.open-sweepstakes-details-drawer', {
          sweepstakesData: displayData,
        });
      } else {
        window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('loyalty-benefits.open-sweepstakes-opt-in-drawer', {
          sweepstakesData: displayData,
        });
      }
    }
  }, [isLoading, hasError, displayData]);

  const handleStepClick = React.useCallback((step) => {
    if (!isLoading && !hasError && displayData?.isOptedIn && windowExists()) {
      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('loyalty-benefits.open-sweepstakes-step-drawer', {
        selectedStep: step,
        sweepstakesData: displayData,
      });
    }
  }, [isLoading, hasError, displayData]);

  const handleFirstStepClick = React.useCallback((step) => {
    if (!isLoading && !hasError && displayData?.isOptedIn && windowExists()) {
      // If first step is not completed, open opt-in drawer instead of step drawer
      if (!step.completed) {
        window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('loyalty-benefits.open-sweepstakes-opt-in-drawer', {
          sweepstakesData: displayData,
        });
      }
      // If completed, do nothing (button is disabled)
    }
  }, [isLoading, hasError, displayData]);

  const headerClasses = SweepstakesEntryCardHeaderProps
    ? getHeaderClasses(
      SweepstakesEntryCardHeaderProps.showOutsideHeaderOn,
      SweepstakesEntryCardHeaderProps.showInsideHeaderOn
    )
    : null;

  if (isLoading) return <RenderLoadingCard />;
  if (isAuthenticated() && hasError) return <RenderErrorCard refresh={refresh} />;
  return (
    <div suppressHydrationWarning>
      {SweepstakesEntryCardHeaderProps?.showOutsideHeaderOn && (
        <div className={headerClasses.outside} data-testid="sweepstakes-header-outside">
          <RenderHeader
            href={SweepstakesEntryCardHeaderProps.href}
            onClick={SweepstakesEntryCardHeaderProps.onClick}
            openNewWindow={SweepstakesEntryCardHeaderProps.openNewWindow}
          />
        </div>
      )}
      <Card grow data-testid="sweepstakes-entry-card">
        <div>
          {SweepstakesEntryCardHeaderProps?.showInsideHeaderOn && (
            <div className={headerClasses.inside} data-testid="sweepstakes-header-inside">
              <RenderHeader
                href={SweepstakesEntryCardHeaderProps.href}
                onClick={SweepstakesEntryCardHeaderProps.onClick}
                openNewWindow={SweepstakesEntryCardHeaderProps.openNewWindow}
              />
            </div>
          )}
          <div className="sui-flex sui-items-center sui-justify-between">
            <div className="sui-flex sui-items-start sui-gap-3 sui-flex-1">
              <RenderLogo
                logoUrl={displayData?.logo}
                logoFallback={logoFallback}
                imgRetryCounter={imgRetryCounter}
              />
              <div className="sui-flex-1">
                <div
                  className="sui-flex sui-flex-col sm:sui-flex-row sui-items-start
                sm:sui-items-center sui-gap-2 sui-mb-2"
                >
                  <RenderStatusBadge status={displayData?.status} />
                </div>
                <RenderTitle title={displayData?.title} />
                <RenderDescription description={displayData?.cardDescription} />
                <RenderExpiration expirationDate={displayData?.expirationDate} />
                {!isAuthenticated() && <RenderSignInButton onClick={gotoLogonModule} />}
              </div>
            </div>
            {isAuthenticated() && displayData && <RenderDetailsButton onClick={handleDetailsClick} />}
          </div>
          {isAuthenticated() && displayData?.steps && displayData.steps.length > 0 && (
            <>
              <RenderProgressSection
                steps={displayData.steps}
                isOptedIn={displayData.isOptedIn}
                onStepClick={handleStepClick}
                onFirstStepClick={handleFirstStepClick}
              />
              <div className="sui-mt-4">
                <RenderCompletionAlert />
              </div>
            </>
          )}
        </div>
      </Card>
    </div>
  );
};

SweepstakesEntryCard.propTypes = {
  SweepstakesEntryCardHeaderProps: PropTypes.shape({
    href: PropTypes.string,
    onClick: PropTypes.func,
    openNewWindow: PropTypes.bool,
    showOutsideHeaderOn: PropTypes.oneOfType([
      PropTypes.oneOf(['MW', 'TW', 'DT']),
      PropTypes.arrayOf(PropTypes.oneOf(['MW', 'TW', 'DT'])),
    ]),
    showInsideHeaderOn: PropTypes.oneOfType([
      PropTypes.oneOf(['MW', 'TW', 'DT']),
      PropTypes.arrayOf(PropTypes.oneOf(['MW', 'TW', 'DT'])),
    ]),
  }),
};

SweepstakesEntryCard.defaultProps = {
  SweepstakesEntryCardHeaderProps: null,
};
